import React, { useEffect, useState, useContext } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";

import { GET_PAGES_LIST, EDIT_PAGE, REMOVE_PAGE } from "../../graphql";
import { Context } from "../../../../context";

import { Gallery } from "../../../../entities/Gallery";
import { GalleryMapper } from "../../dto/GalleryMapper";
import CustomDashboard from "../../../../components/CustomDashboard";

import { Modal, message } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { userPermission } from "../../../../util/user-access";

import { cardText } from "./constant";

const GalleryList = (): JSX.Element => {
    const history = useHistory();
    const match = useRouteMatch();

    const {
        state: { brand: brandData, user },
    } = useContext(Context);
    const brandId = brandData?.id;
    const brandName = brandData?.name;
    const permissions = user?.roles[0].permissions;
    const createAccess = userPermission(permissions, "create:gallery");
    const updateAccess = userPermission(permissions, "update:gallery");
    const deleteAccess = userPermission(permissions, "delete:gallery");
    const [galleryList, setGalleryList] = useState<any>(null);
    const [totalPages, setTotalPages] = useState<number | undefined>(undefined);
    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [search, setSearch] = useState<string>("");

    const { error, refetch, loading, data } = useQuery(GET_PAGES_LIST, {
        skip: !brandId,
        variables: {
            brand_id: brandId,
            search: search,
            tag: `GALLERY-${brandName}`,
            page: page,
            limit: pageSize,
        },
        fetchPolicy: "no-cache",
        partialRefetch: true,
    });

    const [loader, setLoader] = useState<boolean>(false);

    const [editPage] = useMutation(EDIT_PAGE);
    const [removePage] = useMutation(REMOVE_PAGE);

    const handleSearch = (query) => {
        query !== search && setSearch(query);
    };

    const handleAddBtn = () => {
        history.push(`${match.path}/add`);
    };

    const handleFeatured = async (featured: boolean | undefined, id: string) => {
        const value = featured ? false : true;

        const { data: res } = await editPage({
            variables: {
                brand_id: brandId,
                tag: `GALLERY-${brandName}`,
                id: id,
                featured: value,
            },
        });

        if (res.addOrUpdateBrandPage) {
            setLoader(true);
            refetch();
        } else {
            message.error("Something went wrong");
        }
    };

    const handleDelete = (id: string) => {
        const { confirm } = Modal;

        confirm({
            title: "Delete Page?",
            icon: <ExclamationCircleOutlined />,
            content: "Are you sure you want to delete this page?",
            async onOk() {
                try {
                    await removePage({
                        variables: {
                            id: id,
                        },
                    });
                    setLoader(true);
                    refetch();
                    message.success("Successfully deleted page");
                } catch {
                    message.error("Something went wrong");
                } finally {
                    return;
                }
            },
            onCancel() {
                return;
            },
        });
    };

    const handleCard = (
        menuItem: string,
        slug: string,
        id: string,
        featured: boolean | undefined
    ) => {
        if (menuItem === "edit") {
            history.push(`${match.path}/${slug}`, { pageId: id });
        }
        if (menuItem === "featured") {
            handleFeatured(featured, id);
        }
        if (menuItem === "delete") {
            handleDelete(id);
        }
    };

    const handlePageSize = async (pageSize) => {
        setPageSize(pageSize);
    };

    const handlePagination = async (page, pageSize) => {
        setPage(page);
        setPageSize(pageSize);
    };

    useEffect(() => {
        setGalleryList([]);

        if (data && data.searchPages) {
            setLoader(true);

            data.searchPages.totalCount && setTotalPages(data.searchPages.totalCount);

            const { pages } = data.searchPages;
            const res = pages.map((info: Gallery) => Object.assign(GalleryMapper.map({ ...info })));
            setGalleryList(res);
        }

        if (error) {
            message.error("Something went wrong.");
        }

        setLoader(false);
    }, [data, error]);

    return (
        <CustomDashboard
            createAccess={createAccess}
            updateAccess={updateAccess}
            deleteAccess={deleteAccess}
            dashboard="Gallery"
            handleMenu={handleCard}
            loading={loading || loader}
            handleAddBtn={handleAddBtn}
            handleSearch={handleSearch}
            data={galleryList}
            cardText={cardText}
            totalPages={totalPages}
            page={page}
            handlePagination={handlePagination}
            pageSize={pageSize}
            handlePageSize={handlePageSize}
        />
    );
};

export default GalleryList;
